
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import EditFrame from "@/components/EditFrame.vue";
import DangerButton from "../buttons/DangerButton.vue";
import AddButton from "../buttons/AddButton.vue";
import CancelButton from "../buttons/CancelButton.vue";
import DangerDialog from "@/components/dialogs/DangerDialog.vue";
import { Card } from "@/api/dtos/Card.schema";
import { CardVariant } from "@/api/dtos/CardVariant.schema";
import { StationSummary } from "@/api/dtos/StationSummary.schema";
import { Operator } from "@/api/dtos/Operator.schema";
import { deepClone } from "@/misc/helpers";
import api from "@/api";
import { assignStationsToCard, getCardAssignments } from "@/api/cards";
import { StationsModule } from "@/store/modules/StationsModule";
import { showToast } from "@/utils/toastService";
import { getAxiosResponse } from "@/utils/errorUtils";

type CardRow = Card & { stations?: StationSummary[] };
type DialogMode = "create" | "edit";

@Component({
  components: {
    EditFrame,
    CancelButton,
    DangerButton,
    AddButton,
    DangerDialog,
  },
})
export default class Chipcards extends Vue {
  @Prop() operator!: Operator;

  variantLabel(v: CardVariant) {
    return v === CardVariant.SERVICE ? "Service Karte" : "Nutzer Karte";
  }

  readonly headers: {
    text: string;
    value: string;
    align?: "start" | "center" | "end";
  }[] = [
    { text: "Name", value: "name" },
    { text: "Typ", value: "variant" },
    { text: "Service-Karte für", value: "stations" },
    { text: "Aktiv", value: "isActive" },
    { text: "", value: "actions" },
  ];

  cards: CardRow[] = [];
  variantItems = [
    { title: "Service Karte", value: CardVariant.SERVICE },
    { title: "Nutzer Karte", value: CardVariant.BOOKING },
  ];
  stationsAll: StationSummary[] = [];
  loadingStations = false;
  selectedStationsByCard: Record<string, string[]> = {};

  dialog = {
    visible: false,
    mode: "create" as DialogMode,
    valid: false,
    saving: false,
    form: { uid: "", name: "", customId: "", isActive: true } as Card,
  };

  lastVariantSelection = localStorage.getItem("lastVariantSelection") as
    | CardVariant
    | CardVariant.SERVICE;

  deleteDialog = { visible: false, card: null as CardRow | null };

  async mounted(): Promise<void> {
    // await StationsModule.fetchStationSummariesForOperator(this.operator.uuid);
    // await this.$nextTick();
    // await this.loadAssignments();

    this.lastVariantSelection = localStorage.getItem(
      "lastVariantSelection"
    ) as CardVariant;
  }

  @Watch("operator.uuid", { immediate: true })
  async onOperatorUuidChanged(uuid: string): Promise<void> {
    console.log("WATCHED", uuid);
    if (!uuid) return;
    await StationsModule.fetchStationSummariesForOperator(uuid);
    await this.loadAssignments();
  }

  @Watch("operator", { immediate: true })
  syncFromOperator(): void {
    const incoming = (this.operator as any).cards as Card[] | undefined;
    this.cards = deepClone((incoming || []) as CardRow[]);
    this.initializeSelectedStations();
  }

  get stations(): StationSummary[] {
    // return StationsModule.stationSummaries.filter(
    //   (station) => station.operator?.uuid === this.operator.uuid
    // );
    return StationsModule.operatorStations;
  }

  private initializeSelectedStations(): void {
    const map: Record<string, string[]> = {};
    for (const c of this.cards) {
      if (!c?.uid) continue;
      map[c.uid] = (c.stations || []).map((s) => s.uuid);
    }
    this.selectedStationsByCard = map;
  }

  private async loadAssignments(): Promise<void> {
    if (!this.stations.length || !this.cards.length) return;
    const assignments = await getCardAssignments(); // Record<cardUid, string[] stationUuids>
    const byId = new Map(this.stations.map((s) => [s.uuid, s] as const));
    this.cards = this.cards.map((c) => {
      const ids = assignments[c.uid] || [];
      const selected = ids
        .map((id) => byId.get(id))
        .filter((x): x is StationSummary => !!x);
      return { ...c, stations: selected };
    });
    this.initializeSelectedStations();
  }

  variantChanged = (variant: any) => {
    localStorage.setItem("lastVariantSelection", variant as CardVariant);
    this.lastVariantSelection = variant;
  };

  openAddDialog = (): void => {
    this.dialog.mode = "create";
    this.dialog.visible = true;
    this.dialog.form = {
      uid: "",
      name: "",
      customId: "",
      isActive: true,
      variant: this.lastVariantSelection,
    };
    this.dialog.valid = false;
  };

  openEditDialog = (card: CardRow): void => {
    this.dialog.mode = "edit";
    this.dialog.visible = true;
    this.dialog.form = {
      uid: card.uid,
      name: card.name,
      customId: card.customId ?? "",
      isActive: !!card.isActive,
      variant: card.variant,
    };
    this.dialog.valid = true;
  };

  closeDialog = (): void => {
    this.dialog.visible = false;
    this.dialog.saving = false;
  };

  async saveDialog(): Promise<void> {
    const form = this.dialog.form;
    const isCreate = this.dialog.mode === "create";
    this.dialog.saving = true;

    try {
      if (isCreate) {
        const res = await api.OperatorsApi.createCard(this.operator.uuid, form);
        if (res?.status >= 200 && res?.status < 300) {
          showToast({
            message: "Karte erfolgreich erstellt",
            type: "success",
          });
        } else {
          showToast({
            message: "Karte konnte nicht erstellt werden",
            type: "error",
          });
        }
      } else {
        await api.OperatorsApi.updateCard(this.operator.uuid, form);
        showToast({
          message: "Karte erfolgreich aktualisiert",
          type: "success",
        });
      }

      try {
        await this.refresh();
      } catch (e) {
        console.error("refresh() failed after saveDialog:", e);
      }
      this.closeDialog();
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: isCreate
          ? "Fehler beim Erstellen der Karte"
          : "Fehler beim Aktualisieren der Karte",
        type: "error",
      });
      this.dialog.saving = false;
    }
  }

  openDeleteDialog(card: CardRow): void {
    this.deleteDialog.card = card;
    this.deleteDialog.visible = true;
  }

  async confirmDelete(): Promise<void> {
    if (!this.deleteDialog.card) return;
    try {
      const deletedCard = await api.OperatorsApi.deleteCard(
        this.operator.uuid,
        this.deleteDialog.card as Card
      );

      console.log("DeletedCard", deletedCard);

      showToast({
        message: "Karte erfolgreich gelöscht",
        type: "success",
      });

      try {
        await this.refresh();
      } catch (error) {
        console.log("Error", error);
      }
    } catch (e) {
      showToast({
        response: getAxiosResponse(e),
        message: "Fehler beim Löschen der Karte",
        type: "error",
      });
      console.error("Error deleting card:", e);
    } finally {
      this.deleteDialog.visible = false;
      this.deleteDialog.card = null;
    }
  }

  async onToggleActive(card: CardRow): Promise<void> {
    const payload: Card = {
      uid: card.uid,
      name: card.name,
      customId: card.customId,
      isActive: !!card.isActive,
      variant: card.variant,
    };
    try {
      await api.OperatorsApi.updateCard(this.operator.uuid, payload);
    } catch (e) {
      card.isActive = !card.isActive;
    }
  }

  async onStationsChanged(card: CardRow): Promise<void> {
    if (!card?.uid) return;
    const selected = card.stations == null ? [] : card.stations.map((s) => s.uuid);
    try {
      const response = await assignStationsToCard(card.uid, selected);
      if (response?.status >= 200 && response?.status < 300) {
        showToast({
          message: "Servicekarten aktualisiert",
          type: "success",
        });
        return;
      }

      if (response?.status === 502 || response?.status === 504) {
        showToast({
          message: "Station ist offline",
          type: "error",
        });
        await this.refresh();
        return;
      }

      if (response?.status === 409 && response.data.message === "User cards cannot be assigned to stations") {
        const msg = "Diese Karte darf keinen Stationen zugewiesen werden";
        showToast({ message: msg, type: "error" });
        await this.refresh();
        return;
      }

      throw new Error(`Unexpected status code: ${response?.status}`);
    } catch (_) {
      showToast({
        message: "Servicekarten konnten nicht aktualisiert werden",
        type: "error",
      });
      await this.refresh();
    }
  }

  private async refresh(): Promise<void> {
    if ((api as any).OperatorsApi?.getOperator) {
      const fresh = await (api as any).OperatorsApi.getOperator(this.operator.uuid);
      this.cards = deepClone((fresh.cards || []) as CardRow[]);
    } else {
      const incoming = (this.operator as any).cards as Card[] | undefined;
      this.cards = deepClone((incoming || []) as CardRow[]);
    }
    this.initializeSelectedStations();
    await this.loadAssignments();
  }
}
